import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { List, X } from '@phosphor-icons/react';

const Navigation = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en');
  };

  const navLinks = [
    { path: '/', label: t('home') },
    { path: '/about', label: t('about') },
    { path: '/industries', label: t('industries') },
    { path: '/materials', label: t('materials') },
    { path: '/process', label: t('process') },
    { path: '/gallery', label: t('gallery') },
    { path: '/contact', label: t('contact') },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-xl border-b border-black/5 shadow-sm' 
          : 'bg-transparent'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <div className="w-12 h-12 bg-[#C4A661] flex items-center justify-center text-white font-bold text-xl rounded-none">
              DS
            </div>
            <div className={`font-heading text-xl font-light tracking-tight ${
              isScrolled ? 'text-[#0F0F0F]' : 'text-white'
            }`}>
              {isRTL ? 'دار الصفاء' : 'Dar Al Safaa'}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.path.slice(1) || 'home'}`}
                className={`text-sm font-medium tracking-wide transition-all duration-300 ${
                  location.pathname === link.path
                    ? isScrolled ? 'text-[#C4A661]' : 'text-white'
                    : isScrolled ? 'text-[#5C5C55] hover:text-[#C4A661]' : 'text-white/80 hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              data-testid="language-switcher"
              className={`px-4 py-2 text-sm font-medium border transition-all duration-300 ${
                isScrolled
                  ? 'border-black text-black hover:bg-black hover:text-white'
                  : 'border-white text-white hover:bg-white hover:text-black'
              }`}
            >
              {i18n.language === 'en' ? 'AR' : 'EN'}
            </button>

            {/* CTA Button */}
            <Link
              to="/contact"
              data-testid="cta-button"
              className="px-6 py-3 bg-[#0F0F0F] text-white text-sm font-medium hover:bg-[#C4A661] transition-all duration-300"
            >
              {t('getQuote')}
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 ${
              isScrolled ? 'text-black' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X size={28} /> : <List size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-black/5" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-2 text-base font-medium ${
                  location.pathname === link.path
                    ? 'text-[#C4A661]'
                    : 'text-[#5C5C55] hover:text-[#C4A661]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={() => {
                toggleLanguage();
                setIsMobileMenuOpen(false);
              }}
              className="w-full py-3 border border-black text-black text-sm font-medium hover:bg-black hover:text-white transition-all"
            >
              {i18n.language === 'en' ? 'العربية' : 'English'}
            </button>
            <Link
              to="/contact"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full py-3 bg-[#0F0F0F] text-white text-center text-sm font-medium hover:bg-[#C4A661] transition-all"
            >
              {t('getQuote')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
